import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { OrganizationDutyConfiguration } from "../../lib/types";

function DutyForm({ organizationId, initial }: { organizationId: string; initial: OrganizationDutyConfiguration }) {
  const client = useQueryClient();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [interval, setInterval] = useState(String(initial.location_interval_minutes));
  const [distance, setDistance] = useState(String(initial.location_distance_meters));
  const save = useMutation({
    mutationFn: () => api.updateOrganizationDutySettings(organizationId, {
      enabled, location_interval_minutes: Number(interval), location_distance_meters: Number(distance)
    }),
    onSuccess: data => { client.setQueryData(["organization-duty-settings", organizationId], data); }
  });
  return <form className="pa-admin-form" onSubmit={event => { event.preventDefault(); save.mutate(); }} onChange={() => save.reset()}>
    <fieldset className="pa-detail-fields" disabled={save.isPending}>
      <legend>Duty</legend>
      <p className="pa-form-note">Printers have duty enabled by default. Enable it here for other organization types. Start/Stop duty and tracking also follow the organization’s role settings.</p>
      <label><span>Duty feature</span><select value={enabled ? "enabled" : "disabled"} onChange={event => setEnabled(event.target.value === "enabled")}>
        <option value="enabled">Enabled</option><option value="disabled">Disabled</option>
      </select></label>
      <div className="pa-detail-field-grid">
        <label><span>Location upload interval (minutes)</span><input type="number" min={1} max={1440} step={1} required value={interval} onChange={event => setInterval(event.target.value)} /></label>
        <label><span>Location upload distance (meters)</span><input type="number" min={1} max={100000} step={1} required value={distance} onChange={event => setDistance(event.target.value)} /></label>
      </div>
      <p className="pa-form-note">While eligible team members are on duty, a location update is sent when either threshold is reached. Defaults: 15 minutes or more than 1,000 meters.</p>
    </fieldset>
    {save.isError ? <p className="pa-form-error" role="alert">{save.error.message}</p> : null}
    {save.isSuccess ? <p className="pa-form-success" role="status">Duty settings saved.</p> : null}
    <div className="pa-detail-actions"><button type="submit" className="pa-primary-button" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save duty settings"}</button></div>
  </form>;
}
export default function OrganizationDutySettings({ organizationId }: { organizationId: string }) {
  const query = useQuery({ queryKey: ["organization-duty-settings", organizationId], queryFn: () => api.getOrganizationDutySettings(organizationId) });
  return <div className="pa-panel pa-detail-padding pa-org-settings-panel">
    {query.isPending ? <p role="status">Loading duty settings…</p> : null}
    {query.isError ? <><p className="pa-form-error" role="alert">{query.error.message}</p><button className="pa-secondary-button" onClick={() => void query.refetch()}>Retry</button></> : null}
    {query.data ? <DutyForm key={organizationId} organizationId={organizationId} initial={query.data} /> : null}
  </div>;
}
